// ============================================
// VOLUNTEER MODULE - EVENT DETAIL PAGE
// GHL contacts tab + Manual add + Import
// Availability fields shown here (event-specific)
// Status: arrived / pending / not_coming
// ============================================

import React, { useState, useEffect, useCallback } from "react";
import { useVolunteerData } from "../components/VolunteerDataContext";
import { useTheme } from "../components/ThemeContext";
import {
  getVenuesByEvent, getGatesByVenue,
  createVenue, updateVenue, deleteVenue,
  createGate, updateGate, deleteGate,
  getVolunteers, createVolunteer, importVolunteers,
  updateEvent, deleteEvent,
  patchVolunteer, deleteVolunteer,
  getGHLContacts, bulkAddFromGHL,
  downloadVolunteerTemplate,
} from "../services/api";

const sColor = (s,C) => ({ Active:C.green, Draft:C.orange, Completed:C.accent, Cancelled:C.red }[s]||C.muted);
const tColor = (t,C) => ({ Conference:C.accent, Festival:C.orange, Sports:C.green, Religious:C.purple, Music:C.red, Exhibition:C.purple, Other:C.muted }[t]||C.muted);
const regColor = (s,C) => ({ arrived:C.green, pending:C.orange, not_coming:C.red }[s]||C.muted);
const regLabel = (s) => ({ arrived:"Arrived", pending:"Pending", not_coming:"Not Coming" }[s]||s||"pending");

const Badge = ({ text, color }) => (
  <span className="vh-badge" style={{ background:`${color}22`, color, border:`1px solid ${color}44` }}>{text}</span>
);
const Msg = ({ msg, C }) => !msg ? null : (
  <div style={{ padding:"11px 14px", borderRadius:9, marginBottom:14,
    background:msg.type==="success"?C.greenSoft:C.redSoft,
    border:`1px solid ${msg.type==="success"?C.green:C.red}44`,
    color:msg.type==="success"?C.green:C.red, fontSize:13, fontWeight:600 }}>
    {msg.text}
  </div>
);

const EVENT_TYPES  = ["Conference","Festival","Sports","Religious","Music","Exhibition","Other"];
const STATUS_OPTS  = ["Draft","Active","Completed","Cancelled"];
const TIMEZONES    = ["UTC","Asia/Dubai","Asia/Riyadh","Asia/Kuwait","Africa/Lagos","Europe/London","America/New_York","Asia/Karachi","Asia/Kolkata","Asia/Singapore"];
const GATE_TYPES   = ["Entry","Exit","VIP","Parking","Emergency","Media"];
const DESIGNATIONS = ["Joint Secretary","Joint Treasurer","Member","Secretary","Treasurer"];

const inpStyle = (C) => ({ width:"100%", padding:"9px 12px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:8, color:C.text, fontSize:13, outline:"none", boxSizing:"border-box" });

// ── Edit Event Modal ──────────────────────
function EditEventModal({ event, onClose, onSaved, C }) {
  const [form, setForm] = useState({ ...event });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const inp = inpStyle(C);
  const s = (k) => (e) => setForm(f=>({...f,[k]:e.target.value}));

  const handleSave = async () => {
    if(!form.name||!form.eventType||!form.startDate) { setMsg({type:"error",text:"Event Name, Type and Start Date are required."}); return; }
    setSaving(true);
    try { await updateEvent(event.id,form); setMsg({type:"success",text:"Updated!"}); setTimeout(()=>{ onSaved({...event,...form}); onClose(); },700); }
    catch { setMsg({type:"error",text:"Failed to update."}); }
    setSaving(false);
  };

  return (
    <div className="modal-overlay" style={{ background:C.modalBg }}>
      <div className="modal-box" style={{ background:C.card, border:`1px solid ${C.border}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20, paddingBottom:14, borderBottom:`1px solid ${C.border}` }}>
          <div style={{ fontSize:17, fontWeight:800, color:C.text }}>Edit Event</div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.muted, fontSize:22, cursor:"pointer" }}>x</button>
        </div>
        <Msg msg={msg} C={C} />
        <div className="form-grid-2">
          <div style={{ gridColumn:"1 / -1" }}>
            <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase" }}>Event Name *</div>
            <input value={form.name||""} onChange={s("name")} placeholder="Event name" style={inp} />
          </div>
          <div>
            <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase" }}>Event Type *</div>
            <select value={form.eventType||""} onChange={s("eventType")} style={{ ...inp, cursor:"pointer" }}>
              <option value="">Select...</option>
              {EVENT_TYPES.map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase" }}>Status</div>
            <select value={form.status||""} onChange={s("status")} style={{ ...inp, cursor:"pointer" }}>
              {STATUS_OPTS.map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
          <div>
            <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase" }}>Start Date *</div>
            <input type="date" value={form.startDate||""} onChange={s("startDate")} style={inp} />
          </div>
          <div>
            <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase" }}>End Date</div>
            <input type="date" value={form.endDate||""} onChange={s("endDate")} style={inp} />
          </div>
          <div>
            <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase" }}>Start Time</div>
            <input type="time" value={form.startTime||""} onChange={s("startTime")} style={inp} />
          </div>
          <div>
            <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase" }}>End Time</div>
            <input type="time" value={form.endTime||""} onChange={s("endTime")} style={inp} />
          </div>
          <div style={{ gridColumn:"1 / -1" }}>
            <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase" }}>Timezone</div>
            <select value={form.timezone||"UTC"} onChange={s("timezone")} style={{ ...inp, cursor:"pointer" }}>
              {TIMEZONES.map(o=><option key={o}>{o}</option>)}
            </select>
          </div>
          <div style={{ gridColumn:"1 / -1" }}>
            <div style={{ fontSize:11, color:C.muted, marginBottom:5, fontWeight:600, textTransform:"uppercase" }}>Description</div>
            <textarea value={form.description||""} onChange={s("description")} rows={2} style={{ ...inp, resize:"vertical" }} />
          </div>
        </div>
        <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
          <button onClick={onClose} style={{ padding:"10px 18px", background:"none", border:`1px solid ${C.border}`, borderRadius:10, color:C.muted, fontSize:13, cursor:"pointer" }}>Cancel</button>
          <button onClick={handleSave} disabled={saving} style={{ padding:"10px 24px", background:C.accent, border:"none", borderRadius:10, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>
            {saving?"Saving...":"Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Gate Form ─────────────────────────────
function GateForm({ initial, onSave, onCancel, saving, C }) {
  const [form, setForm] = useState(initial || { name:"", gateType:"Entry", minVolunteers:"", maxVolunteers:"", notes:"" });
  const [equipment, setEquipment] = useState(initial?.equipment || []);
  const inp = inpStyle(C);
  const s = (k) => (e) => setForm(f=>({...f,[k]:e.target.value}));

  return (
    <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:11, padding:16, marginTop:10, boxSizing:"border-box", width:"100%", overflow:"hidden" }}>
      <div className="form-grid-2">
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Gate Name *</div>
          <input value={form.name||""} onChange={s("name")} placeholder="e.g. Gate A" style={inp} />
        </div>
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Gate Type</div>
          <select value={form.gateType||"Entry"} onChange={s("gateType")} style={{ ...inp, cursor:"pointer" }}>
            {GATE_TYPES.map(g=><option key={g}>{g}</option>)}
          </select>
        </div>
        <div style={{ padding:10, background:C.greenSoft, border:`1px solid ${C.green}33`, borderRadius:9 }}>
          <div style={{ fontSize:10, color:C.green, marginBottom:4, fontWeight:700, textTransform:"uppercase" }}>Min Volunteers</div>
          <input type="number" value={form.minVolunteers||""} onChange={s("minVolunteers")} placeholder="0" min="0"
            style={{ ...inp, fontSize:16, fontWeight:800, textAlign:"center", border:`1px solid ${C.green}66` }} />
        </div>
        <div style={{ padding:10, background:C.orangeSoft, border:`1px solid ${C.orange}33`, borderRadius:9 }}>
          <div style={{ fontSize:10, color:C.orange, marginBottom:4, fontWeight:700, textTransform:"uppercase" }}>Max Volunteers</div>
          <input type="number" value={form.maxVolunteers||""} onChange={s("maxVolunteers")} placeholder="0" min="0"
            style={{ ...inp, fontSize:16, fontWeight:800, textAlign:"center", border:`1px solid ${C.orange}66` }} />
        </div>
        <div style={{ gridColumn:"1 / -1" }}>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
            <div style={{ fontSize:11, color:C.muted, fontWeight:600, textTransform:"uppercase" }}>Equipment</div>
            <button onClick={()=>setEquipment(e=>[...e,{id:Date.now(),name:"",quantity:"1"}])}
              style={{ padding:"4px 10px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:6, color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>+ Add</button>
          </div>
          {equipment.map(eq=>(
            <div key={eq.id} style={{ display:"grid", gridTemplateColumns:"1fr 80px 32px", gap:6, marginBottom:6 }}>
              <input value={eq.name} onChange={e=>setEquipment(prev=>prev.map(x=>x.id===eq.id?{...x,name:e.target.value}:x))} placeholder="Equipment name" style={{ ...inp, padding:"7px 10px" }} />
              <input type="number" value={eq.quantity} onChange={e=>setEquipment(prev=>prev.map(x=>x.id===eq.id?{...x,quantity:e.target.value}:x))} min="1" style={{ ...inp, padding:"7px 8px", textAlign:"center" }} />
              <button onClick={()=>setEquipment(prev=>prev.filter(x=>x.id!==eq.id))} style={{ background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:6, color:C.red, fontSize:11, cursor:"pointer" }}>x</button>
            </div>
          ))}
        </div>
      </div>
      <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:12 }}>
        <button onClick={onCancel} style={{ padding:"7px 14px", background:"none", border:`1px solid ${C.border}`, borderRadius:8, color:C.muted, fontSize:12, cursor:"pointer" }}>Cancel</button>
        <button onClick={()=>form.name&&onSave({...form,equipment})} disabled={saving||!form.name}
          style={{ padding:"7px 16px", background:form.name?C.accent:C.border, border:"none", borderRadius:8, color:form.name?"#000":C.muted, fontSize:12, fontWeight:700, cursor:form.name?"pointer":"not-allowed" }}>
          {saving?"Saving...":"Save Gate"}
        </button>
      </div>
    </div>
  );
}

// ── Venue Form ────────────────────────────
function VenueForm({ initial, onSave, onCancel, saving, C }) {
  const [form, setForm] = useState(initial || { name:"", address:"", capacity:"", lat:"", lng:"", mapUrl:"" });
  const inp = inpStyle(C);
  const s = (k) => (e) => setForm(f=>({...f,[k]:e.target.value}));

  return (
    <div style={{ background:C.bg, border:`1px dashed ${C.border}`, borderRadius:12, padding:20 }}>
      <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:14 }}>{initial?"Edit Venue":"Add New Venue"}</div>
      <div className="form-grid-2">
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Venue Name *</div>
          <input value={form.name||""} onChange={s("name")} placeholder="e.g. Main Hall" style={inp} />
        </div>
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Capacity</div>
          <input type="number" value={form.capacity||""} onChange={s("capacity")} placeholder="e.g. 5000" style={inp} />
        </div>
        <div style={{ gridColumn:"1 / -1" }}>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Address *</div>
          <input value={form.address||""} onChange={s("address")} placeholder="Full address" style={inp} />
        </div>
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Latitude</div>
          <input value={form.lat||""} onChange={s("lat")} placeholder="e.g. 25.2285" style={inp} />
        </div>
        <div>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Longitude</div>
          <input value={form.lng||""} onChange={s("lng")} placeholder="e.g. 55.2867" style={inp} />
        </div>
        <div style={{ gridColumn:"1 / -1" }}>
          <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Map URL</div>
          <input value={form.mapUrl||""} onChange={s("mapUrl")} placeholder="https://maps.google.com/..." style={inp} />
        </div>
      </div>
      <div style={{ display:"flex", justifyContent:"flex-end", gap:8, marginTop:14 }}>
        <button onClick={onCancel} style={{ padding:"8px 16px", background:"none", border:`1px solid ${C.border}`, borderRadius:9, color:C.muted, fontSize:13, cursor:"pointer" }}>Cancel</button>
        <button onClick={()=>(form.name&&form.address)&&onSave(form)} disabled={saving||!form.name||!form.address}
          style={{ padding:"8px 20px", background:(form.name&&form.address)?C.accent:C.border, border:"none", borderRadius:9, color:(form.name&&form.address)?"#000":C.muted, fontSize:13, fontWeight:700, cursor:(form.name&&form.address)?"pointer":"not-allowed" }}>
          {saving?"Saving...":"Save Venue"}
        </button>
      </div>
    </div>
  );
}

// ── Venue Section ─────────────────────────
function VenueSection({ venue, eventId, onVenueUpdated, onVenueDeleted, C }) {
  const [gates, setGates]          = useState([]);
  const [loading, setLoading]      = useState(false);
  const [expanded, setExpanded]    = useState(true);
  const [showEditV, setEditV]      = useState(false);
  const [showAddG, setAddG]        = useState(false);
  const [editingGate, setEditingG] = useState(null);
  const [savingV, setSavingV]      = useState(false);
  const [savingG, setSavingG]      = useState(false);
  const [msg, setMsg]              = useState(null);

  const loadGates = useCallback(() => {
    setLoading(true);
    getGatesByVenue(venue.id).then(r=>setGates(r.data.gates||[])).catch(()=>setGates([])).finally(()=>setLoading(false));
  }, [venue.id]);

  useEffect(()=>{ loadGates(); },[loadGates]);
  const flash = (m) => { setMsg(m); setTimeout(()=>setMsg(null),3000); };

  const saveVenue = async (data) => { setSavingV(true); try { await updateVenue(venue.id,{...data,eventId}); onVenueUpdated({...venue,...data}); setEditV(false); flash({type:"success",text:"Venue updated!"}); } catch { flash({type:"error",text:"Failed."}); } setSavingV(false); };
  const delVenue  = async () => { if(!window.confirm(`Delete "${venue.name}"?`)) return; try { await deleteVenue(venue.id); onVenueDeleted(venue.id); } catch { flash({type:"error",text:"Failed."}); } };
  const addGate   = async (data) => { setSavingG(true); try { await createGate({...data,venueId:venue.id,venueName:venue.name,eventId}); loadGates(); setAddG(false); flash({type:"success",text:"Gate added!"}); } catch { flash({type:"error",text:"Failed."}); } setSavingG(false); };
  const editGate  = async (data) => { setSavingG(true); try { await updateGate(editingGate.id,data); loadGates(); setEditingG(null); flash({type:"success",text:"Gate updated!"}); } catch { flash({type:"error",text:"Failed."}); } setSavingG(false); };
  const delGate   = async (gate) => { if(!window.confirm(`Delete gate "${gate.name}"?`)) return; try { await deleteGate(gate.id); loadGates(); } catch { flash({type:"error",text:"Failed."}); } };

  return (
    <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:14, marginBottom:12, overflow:"hidden" }}>
      {msg && <div style={{ padding:"8px 18px" }}><Msg msg={msg} C={C} /></div>}
      {showEditV ? (
        <div style={{ padding:16 }}><VenueForm initial={venue} onSave={saveVenue} onCancel={()=>setEditV(false)} saving={savingV} C={C} /></div>
      ) : (
        <>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"14px 18px", background:expanded?C.accentSoft:"transparent" }}>
            <div onClick={()=>setExpanded(e=>!e)} style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer", flex:1 }}>
              <span style={{ fontSize:15 }}>🏟</span>
              <div>
                <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{venue.name}</div>
                <div style={{ fontSize:12, color:C.muted }}>📍 {venue.address}{venue.capacity&&<> · Capacity: {Number(venue.capacity).toLocaleString()}</>}</div>
              </div>
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:5, flexWrap:"wrap" }}>
              {venue.mapUrl && <a href={venue.mapUrl} target="_blank" rel="noreferrer" onClick={e=>e.stopPropagation()} style={{ fontSize:11, color:C.accent, textDecoration:"none", fontWeight:600, padding:"4px 8px" }}>Map</a>}
              <Badge text={`${gates.length} gates`} color={C.accent} />
              <button onClick={()=>setEditV(true)} style={{ padding:"5px 10px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>Edit</button>
              <button onClick={delVenue} style={{ padding:"5px 10px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:7, color:C.red, fontSize:11, fontWeight:700, cursor:"pointer" }}>Delete</button>
              <button onClick={()=>setAddG(g=>!g)} style={{ padding:"5px 10px", background:C.greenSoft, border:`1px solid ${C.green}44`, borderRadius:7, color:C.green, fontSize:11, fontWeight:700, cursor:"pointer" }}>+ Gate</button>
              <span onClick={()=>setExpanded(e=>!e)} style={{ color:C.muted, fontSize:14, transform:expanded?"rotate(180deg)":"rotate(0)", display:"inline-block", transition:"transform 0.2s", cursor:"pointer" }}>v</span>
            </div>
          </div>
          {expanded && (
            <div style={{ padding:"0 18px 18px" }}>
              {showAddG && <GateForm onSave={addGate} onCancel={()=>setAddG(false)} saving={savingG} C={C} />}
              {loading ? (
                <div style={{ padding:20, textAlign:"center", color:C.muted, fontSize:13 }}>Loading gates...</div>
              ) : gates.length===0 ? (
                <div style={{ padding:"12px 0", fontSize:13, color:C.muted }}>No gates yet. Click "+ Gate" above.</div>
              ) : (
                <>
                  <div style={{ fontSize:10, color:C.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.5px", marginBottom:8, marginTop:12 }}>Gates ({gates.length})</div>
                  {editingGate && (
                    <div style={{ marginBottom:12, padding:14, background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:11 }}>
                      <div style={{ fontSize:11, color:C.accent, fontWeight:700, marginBottom:8, textTransform:"uppercase" }}>Editing Gate: {editingGate.name}</div>
                      <GateForm initial={editingGate} onSave={editGate} onCancel={()=>setEditingG(null)} saving={savingG} C={C} />
                    </div>
                  )}
                  <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(200px, 1fr))", gap:10 }}>
                    {gates.map((gate,i) => (
                      <div key={i} style={{ background:C.bg, border:`1px solid ${editingGate?.id===gate.id?C.accent:C.border}`, borderRadius:12, padding:12, boxSizing:"border-box", overflow:"hidden" }}>
                        {editingGate?.id===gate.id ? (
                          <div style={{ textAlign:"center", padding:"12px 0", color:C.accent, fontSize:12, fontWeight:600 }}>Editing above...</div>
                        ) : (
                          <>
                            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
                              <div style={{ flex:1, minWidth:0 }}>
                                <div style={{ fontSize:12, fontWeight:700, color:C.text, wordBreak:"break-word" }}>{gate.name}</div>
                                <Badge text={gate.gateType||"Entry"} color={C.accent} />
                              </div>
                              <div style={{ display:"flex", gap:4, flexShrink:0, marginLeft:6 }}>
                                <button onClick={()=>setEditingG(gate)} style={{ padding:"4px 8px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:6, color:C.accent, fontSize:10, fontWeight:700, cursor:"pointer" }}>Edit</button>
                                <button onClick={()=>delGate(gate)} style={{ padding:"4px 8px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:6, color:C.red, fontSize:10, fontWeight:700, cursor:"pointer" }}>Del</button>
                              </div>
                            </div>
                            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:6, marginBottom:6 }}>
                              <div style={{ padding:"7px", background:C.greenSoft, border:`1px solid ${C.green}33`, borderRadius:7, textAlign:"center" }}>
                                <div style={{ fontSize:16, fontWeight:800, color:C.green }}>{gate.minVolunteers||0}</div>
                                <div style={{ fontSize:9, color:C.green, fontWeight:700, textTransform:"uppercase" }}>Min</div>
                              </div>
                              <div style={{ padding:"7px", background:C.orangeSoft, border:`1px solid ${C.orange}33`, borderRadius:7, textAlign:"center" }}>
                                <div style={{ fontSize:16, fontWeight:800, color:C.orange }}>{gate.maxVolunteers||0}</div>
                                <div style={{ fontSize:9, color:C.orange, fontWeight:700, textTransform:"uppercase" }}>Max</div>
                              </div>
                            </div>
                            {(gate.equipment||[]).length>0&&(
                              <div style={{ display:"flex", flexWrap:"wrap", gap:3, marginTop:4 }}>
                                {gate.equipment.map((eq,j)=>(
                                  <span key={j} style={{ padding:"2px 6px", background:C.accentSoft, border:`1px solid ${C.accent}33`, borderRadius:5, fontSize:9, color:C.accent, fontWeight:600, whiteSpace:"nowrap" }}>{eq.name}x{eq.quantity}</span>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ============================================
// DYNAMIC FILTER BUILDER
// User picks field + types value themselves
// Multiple rows, all combined as AND logic
// ============================================
function FilterBuilder({ fields, rows, onChange, C }) {
  const addRow    = () => onChange([...rows, { id:Date.now(), field:fields[0]?.key||"", value:"" }]);
  const removeRow = (id) => onChange(rows.filter(r=>r.id!==id));
  const updateRow = (id, k, v) => onChange(rows.map(r=>r.id===id?{...r,[k]:v}:r));
  const inp = { padding:"7px 10px", background:C.inputBg, border:`1px solid ${C.border}`, borderRadius:7, color:C.text, fontSize:12, outline:"none" };
  return (
    <div style={{ background:C.bg, border:`1px solid ${C.border}`, borderRadius:11, padding:14, marginBottom:12 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
        <div style={{ fontSize:11, fontWeight:700, color:C.accent, textTransform:"uppercase", letterSpacing:"0.5px" }}>
          Field Filters
          {rows.length>0&&<span style={{ background:C.accent, color:"#000", borderRadius:99, padding:"1px 8px", fontSize:10, marginLeft:8, fontWeight:800 }}>{rows.length}</span>}
        </div>
        <div style={{ display:"flex", gap:6 }}>
          {rows.length>0&&<button onClick={()=>onChange([])} style={{ padding:"4px 10px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:6, color:C.red, fontSize:11, fontWeight:600, cursor:"pointer" }}>Clear All</button>}
          <button onClick={addRow} style={{ padding:"4px 12px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:6, color:C.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>+ Add Filter</button>
        </div>
      </div>
      {rows.length===0&&<div style={{ fontSize:12, color:C.muted, fontStyle:"italic", textAlign:"center", padding:"6px 0" }}>Click "+ Add Filter" to filter by any field</div>}
      {rows.map(row=>{
        const fd = fields.find(f=>f.key===row.field)||fields[0]||{};
        return (
          <div key={row.id} style={{ display:"flex", gap:8, marginBottom:8, alignItems:"center", flexWrap:"wrap" }}>
            <select value={row.field} onChange={e=>updateRow(row.id,"field",e.target.value)} style={{ ...inp, flex:"0 0 180px", cursor:"pointer" }}>
              {fields.map(f=><option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
            {fd.type==="select"?(
              <select value={row.value} onChange={e=>updateRow(row.id,"value",e.target.value)} style={{ ...inp, flex:1, minWidth:140, cursor:"pointer" }}>
                <option value="">Any</option>
                {(fd.options||[]).map(o=><option key={o}>{o}</option>)}
              </select>
            ):fd.type==="date"?(
              <input type="date" value={row.value} onChange={e=>updateRow(row.id,"value",e.target.value)} style={{ ...inp, flex:1, minWidth:140 }} />
            ):(
              <input type="text" value={row.value} onChange={e=>updateRow(row.id,"value",e.target.value)} placeholder={`Filter by ${fd.label}...`} style={{ ...inp, flex:1, minWidth:140 }} />
            )}
            <button onClick={()=>removeRow(row.id)} style={{ padding:"6px 10px", background:C.redSoft, border:`1px solid ${C.red}33`, borderRadius:7, color:C.red, fontSize:14, fontWeight:700, cursor:"pointer", flexShrink:0 }}>x</button>
          </div>
        );
      })}
    </div>
  );
}

function applyFilterRows(items, rows, getVal) {
  if(!rows||!rows.length) return items;
  return items.filter(item=>rows.every(row=>{
    if(!row.value) return true;
    return String(getVal(item,row.field)||"").toLowerCase().includes(row.value.toLowerCase());
  }));
}

// ── GHL Contacts Tab ──────────────────────
function GHLContactsTab({ event, onAdded, C }) {
  const [contacts, setContacts]     = useState([]);
  const [loading, setLoading]       = useState(false);
  const [query, setQuery]           = useState("");
  const [selected, setSelected]     = useState(new Set());
  const [adding, setAdding]         = useState(false);
  const [msg, setMsg]               = useState(null);
  const [meta, setMeta]             = useState({});
  const [nextAfter, setNextAfter]   = useState("");
  const [nextAfterId, setNextAfterId] = useState("");
  const [filterRows, setFilterRows] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  // GHL returns customFields as [{id, value}] — map by known IDs
  const GHL_IDS = {
    "e8nBqbRxbODvwLXVEi50":"its_id", "pTgOkLhQrqDpOuWX6YRk":"jamiat",
    "LOlX2RmXLmzBt6MueklO":"jamaat", "z9kDrA4q6qz1Q7WqlXEn":"designation",
    "WDHZZMMycWUPGCn07vbH":"zone",   "DQE0kPotAaMsWnvQEHrL":"coordinator",
    "gce8jYojXJdESlGiqg6T":"registration_status",
    "dF6DiC6dCvYIWxE7R1U1":"event_name",
    "hU8Ggn88QKEpztjrm4df":"arrival_date",
    "QM5yoxwiPGrPzyXcmINB":"departure_date",
    "h2DYOvTvTvcx8fqNblsC":"skills",
    "cHuKk2Wv7FSH4gTaFUeS":"preferred_duty_types",
    "G9x3BBXct0t8V3zwOYZT":"venue_name",
    "Z44XhYibf4plH03DGNWH":"assignment_status",
  };

  const ghlVal = (c, key) => {
    if(key==="name")    return [c.firstName,c.lastName].filter(Boolean).join(" ")||c.contactName||"";
    if(key==="email")   return c.email||"";
    if(key==="phone")   return c.phone||"";
    if(key==="city")    return c.city||"";
    if(key==="country") return c.country||"";
    if(key==="source")  return c.source||"";
    if(key==="tags")    return (c.tags||[]).join(", ");
    // Build field map from [{id,value}] array
    const fm = {};
    (c.customFields||[]).forEach(f=>{ const k=GHL_IDS[f.id]; if(k) fm[k]=f.value; });
    return String(fm[key]||"");
  };

  const GHL_FILTER_FIELDS = [
    {key:"name",        label:"Name"},
    {key:"email",       label:"Email"},
    {key:"phone",       label:"Phone"},
    {key:"its_id",      label:"ITS ID"},
    {key:"jamiat",      label:"Jamiat"},
    {key:"jamaat",      label:"Jamaat"},
    {key:"designation", label:"Designation"},
    {key:"zone",        label:"Zone"},
    {key:"coordinator", label:"Coordinator"},
    {key:"tags",        label:"Tags"},
    {key:"city",        label:"City"},
    {key:"country",     label:"Country"},
    {key:"source",      label:"Source"},
    {key:"registration_status", label:"Reg Status"},
    {key:"event_name",  label:"Event Name"},
  ];

  const load = async (reset=false) => {
    setLoading(true);
    try {
      const params = { limit:100 };
      if(query) params.query = query;
      if(!reset && nextAfter) { params.startAfter = nextAfter; params.startAfterId = nextAfterId; }
      const r = await getGHLContacts(params);
      const newContacts = r.data.contacts||[];
      setContacts(prev => reset ? newContacts : [...prev,...newContacts]);
      setMeta(r.data.meta||{});
      if(r.data.meta?.startAfter)   setNextAfter(r.data.meta.startAfter);
      if(r.data.meta?.startAfterId) setNextAfterId(r.data.meta.startAfterId);
    } catch(e) {
      setMsg({type:"error",text:`GHL error: ${e.response?.data?.message||e.message}`});
    }
    setLoading(false);
  };

  useEffect(()=>{ load(true); },[]); // eslint-disable-line

  // Search + dynamic filter rows
  const preFiltered = contacts.filter(c => {
    if(!query.trim()) return true;
    const q = query.toLowerCase();
    return ghlVal(c,"name").toLowerCase().includes(q)
      || ghlVal(c,"phone").toLowerCase().includes(q)
      || ghlVal(c,"email").toLowerCase().includes(q)
      || ghlVal(c,"its_id").toLowerCase().includes(q);
  });

  const filtered = applyFilterRows(preFiltered, filterRows, ghlVal);

  const toggle    = (id) => setSelected(prev=>{ const n=new Set(prev); n.has(id)?n.delete(id):n.add(id); return n; });
  const selectAll = ()   => setSelected(new Set(filtered.map(c=>c.id)));
  const clearAll  = ()   => setSelected(new Set());

  const handleBulkAdd = async () => {
    if(selected.size===0) return;
    setAdding(true);
    try {
      const chosen = contacts.filter(c=>selected.has(c.id));
      const r = await bulkAddFromGHL({ contacts:chosen, eventId:event.id, eventName:event.name });
      setMsg({type:"success",text:r.data.message});
      setSelected(new Set());
      setTimeout(()=>{ setMsg(null); onAdded(); },1500);
    } catch(e) { setMsg({type:"error",text:"Failed to add volunteers."}); }
    setAdding(false);
  };

  return (
    <div>
      <div style={{ padding:"10px 14px", background:C.accentSoft, border:`1px solid ${C.accent}33`, borderRadius:9, marginBottom:12, fontSize:12, color:C.accent }}>
        Select contacts to add as volunteers for <strong>{event.name}</strong>
      </div>
      <Msg msg={msg} C={C} />

      {/* Search + filter toggle */}
      <div style={{ display:"flex", gap:8, marginBottom:8, flexWrap:"wrap" }}>
        <input value={query} onChange={e=>setQuery(e.target.value)} onKeyDown={e=>e.key==="Enter"&&load(true)}
          placeholder="Quick search: name, email, phone, ITS ID..."
          style={{ flex:1, minWidth:200, padding:"9px 12px", background:C.card, border:`1px solid ${C.border}`, borderRadius:9, color:C.text, fontSize:13, outline:"none" }} />
        <button onClick={()=>load(true)} style={{ padding:"9px 16px", background:C.accent, border:"none", borderRadius:9, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>Search</button>
        <button onClick={()=>setShowFilters(v=>!v)}
          style={{ padding:"9px 14px", background:filterRows.length>0||showFilters?C.accentSoft:C.card, border:`1px solid ${filterRows.length>0?C.accent:C.border}`, borderRadius:9, color:filterRows.length>0?C.accent:C.muted, fontSize:13, fontWeight:600, cursor:"pointer" }}>
          Field Filters{filterRows.length>0?` (${filterRows.length})`:""}
        </button>
      </div>

      {/* Dynamic filter builder */}
      {showFilters && <FilterBuilder fields={GHL_FILTER_FIELDS} rows={filterRows} onChange={setFilterRows} C={C} />}

      {filterRows.length>0 && (
        <div style={{ fontSize:12, color:C.muted, marginBottom:8 }}>
          Showing <strong style={{color:C.text}}>{filtered.length}</strong> of <strong style={{color:C.text}}>{contacts.length}</strong> loaded
          {meta.total&&<> · <strong style={{color:C.accent}}>{meta.total}</strong> total contacts</>}
        </div>
      )}

      {/* Bulk action bar */}
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10, flexWrap:"wrap", gap:8 }}>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          <button onClick={selectAll} style={{ padding:"6px 12px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:7, color:C.accent, fontSize:12, fontWeight:600, cursor:"pointer" }}>
            Select All ({filtered.length})
          </button>
          <button onClick={clearAll} style={{ padding:"6px 12px", background:"none", border:`1px solid ${C.border}`, borderRadius:7, color:C.muted, fontSize:12, cursor:"pointer" }}>Clear</button>
          <span style={{ fontSize:12, color:C.text, fontWeight:600 }}>{selected.size} selected</span>
        </div>
        <button onClick={handleBulkAdd} disabled={adding||selected.size===0}
          style={{ padding:"8px 20px", background:selected.size>0?C.green:C.border, border:"none", borderRadius:9, color:selected.size>0?"#000":C.muted, fontSize:13, fontWeight:700, cursor:selected.size>0?"pointer":"not-allowed" }}>
          {adding?"Adding...":selected.size>0?`Add ${selected.size} as Volunteers`:"Select contacts above"}
        </button>
      </div>

      {/* Contacts table */}
      {loading && contacts.length===0 ? (
        <div style={{ padding:40, textAlign:"center", color:C.muted }}>Loading contacts...</div>
      ) : filtered.length===0 && !loading ? (
        <div style={{ padding:40, textAlign:"center", color:C.muted, background:C.card, border:`1px solid ${C.border}`, borderRadius:12 }}>
          {contacts.length===0 ? "No contacts loaded yet." : "No contacts match the current filters."}
        </div>
      ) : (
        <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:16, overflow:"hidden" }}>
          <div style={{ padding:"10px 14px", borderBottom:`1px solid ${C.border}`, fontSize:12, color:C.muted }}>
            {filtered.length} contacts shown · {meta.total||"?"} total contacts
          </div>
          <div className="responsive-table">
            <table>
              <thead>
                <tr style={{ background:C.bg }}>
                  {["","Name","Phone","Email","ITS ID","Jamiat","Jamaat","Designation","Zone","Coordinator","Tags"].map(h=>(
                    <th key={h} style={{ padding:"10px 12px", textAlign:"left", fontSize:10, fontWeight:700, color:C.muted, letterSpacing:"0.7px", textTransform:"uppercase", borderBottom:`1px solid ${C.border}`, whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((c,i)=>{
                  const name  = ghlVal(c,"name");
                  const isSel = selected.has(c.id);
                  return (
                    <tr key={i} onClick={()=>toggle(c.id)} style={{ borderBottom:`1px solid ${C.border}`, cursor:"pointer", background:isSel?`${C.accent}11`:"transparent", transition:"background 0.15s" }}>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ width:18, height:18, borderRadius:4, border:`2px solid ${isSel?C.accent:C.border}`, background:isSel?C.accent:"transparent", display:"flex", alignItems:"center", justifyContent:"center" }}>
                          {isSel&&<span style={{ color:"#000", fontSize:11, fontWeight:800 }}>v</span>}
                        </div>
                      </td>
                      <td style={{ padding:"10px 12px" }}><div style={{ fontSize:13, fontWeight:700, color:C.text, whiteSpace:"nowrap" }}>{name||"—"}</div></td>
                      <td style={{ padding:"10px 12px", fontSize:12, color:C.green, fontWeight:600, whiteSpace:"nowrap" }}>{ghlVal(c,"phone")||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.muted }}>{ghlVal(c,"email")||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.muted, fontFamily:"monospace" }}>{ghlVal(c,"its_id")||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.text }}>{ghlVal(c,"jamiat")||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.text }}>{ghlVal(c,"jamaat")||"—"}</td>
                      <td style={{ padding:"10px 12px" }}>{ghlVal(c,"designation")?<Badge text={ghlVal(c,"designation")} color={C.purple}/>:<span style={{fontSize:11,color:C.muted}}>—</span>}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.text }}>{ghlVal(c,"zone")||"—"}</td>
                      <td style={{ padding:"10px 12px", fontSize:11, color:C.text }}>{ghlVal(c,"coordinator")||"—"}</td>
                      <td style={{ padding:"10px 12px" }}>
                        <div style={{ display:"flex", flexWrap:"wrap", gap:3 }}>
                          {(c.tags||[]).slice(0,3).map((t,j)=>(
                            <span key={j} style={{ padding:"2px 6px", background:C.accentSoft, border:`1px solid ${C.accent}33`, borderRadius:5, fontSize:9, color:C.accent, fontWeight:600 }}>{t}</span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {meta.nextPageUrl && (
            <div style={{ padding:"12px 16px", borderTop:`1px solid ${C.border}`, textAlign:"center" }}>
              <button onClick={()=>load(false)} disabled={loading} style={{ padding:"8px 20px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:9, color:C.accent, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                {loading?"Loading...":"Load More Contacts"}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
// ── Add Volunteer Modal ───────────────────
function AddVolunteerModal({ event, onClose, onAdded, C }) {
  const [tab, setTab] = useState("ghl");
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [msg, setMsg] = useState(null);
  const emptyForm = { itsId:"", fullName:"", email:"", whatsappNumber:"", phoneNumber:"", jamiat:"", jamaat:"", designation:"Member", zone:"", coordinator:"", age:"", registrationStatus:"pending", arrivalDate:"", departureDate:"", availabilityNotes:"" };
  const [form, setForm] = useState(emptyForm);
  const inp = inpStyle(C);
  const s = (k) => (e) => setForm(f=>({...f,[k]:e.target.value}));

  const handleSave = async () => {
    const req = ["fullName","email","whatsappNumber","phoneNumber","jamiat","jamaat","designation","zone"];
    const labels = { fullName:"Full Name", email:"Email", whatsappNumber:"WhatsApp Number", phoneNumber:"Phone Number", jamiat:"Jamiat", jamaat:"Jamaat", designation:"Designation", zone:"Zone" };
    for(const k of req) { if(!form[k]) { setMsg({type:"error",text:`${labels[k]} is required.`}); return; } }
    if(!form.itsId) { setMsg({type:"error",text:"ITS ID is required."}); return; }
    if(form.itsId.length!==8||!/^\d+$/.test(form.itsId)) { setMsg({type:"error",text:"ITS ID must be exactly 8 digits."}); return; }
    setSaving(true);
    try {
      await createVolunteer({...form, eventId:event.id, eventName:event.name});
      setMsg({type:"success",text:`${form.fullName} added!`});
      setTimeout(()=>onAdded(),800);
    } catch(e) { setMsg({type:"error",text:e.response?.data?.message||"Failed to add volunteer."}); }
    setSaving(false);
  };

  const handleImport = async (e) => {
    const file = e.target.files[0]; if(!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append("file",file); fd.append("eventId",event.id); fd.append("eventName",event.name);
      const r = await importVolunteers(fd);
      setMsg({type:"success",text:r.data.message});
      setTimeout(()=>onAdded(),1500);
    } catch { setMsg({type:"error",text:"Import failed."}); }
    setImporting(false); e.target.value="";
  };

  const fld = (label, k, ph, type="text", req=false) => (
    <div>
      <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>{label}{req&&<span style={{color:C.red}}> *</span>}</div>
      <input type={type} value={form[k]||""} onChange={s(k)} placeholder={ph} style={inp} />
    </div>
  );

  return (
    <div className="modal-overlay" style={{ background:C.modalBg }}>
      <div className="modal-box" style={{ background:C.card, border:`1px solid ${C.border}`, maxWidth:700 }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:16, paddingBottom:12, borderBottom:`1px solid ${C.border}`, position:"sticky", top:0, background:C.card, zIndex:10 }}>
          <div>
            <div style={{ fontSize:16, fontWeight:800, color:C.text }}>Add Volunteers</div>
            <div style={{ fontSize:12, color:C.accent, marginTop:2 }}>Event: {event.name}</div>
          </div>
          <button onClick={onClose} style={{ background:"none", border:"none", color:C.muted, fontSize:22, cursor:"pointer" }}>x</button>
        </div>
        <div style={{ display:"flex", gap:3, marginBottom:16, background:C.bg, padding:3, borderRadius:9, width:"fit-content", border:`1px solid ${C.border}` }}>
          {[{id:"ghl",label:"From Contacts"},{id:"manual",label:"Manual"},{id:"import",label:"Import File"}].map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{ padding:"6px 16px", background:tab===t.id?C.accent:"transparent", border:"none", borderRadius:7, color:tab===t.id?"#000":C.muted, fontSize:12, fontWeight:tab===t.id?700:500, cursor:"pointer" }}>
              {t.label}
            </button>
          ))}
        </div>
        <Msg msg={msg} C={C} />

        {tab==="ghl" && <GHLContactsTab event={event} onAdded={()=>{ onAdded(); onClose(); }} C={C} />}

        {tab==="manual" && (
          <>
            <div className="form-grid-2">
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>ITS ID (8 digits) <span style={{color:C.red}}>*</span></div>
                <input value={form.itsId||""} onChange={e=>setForm(f=>({...f,itsId:e.target.value.replace(/\D/g,"").slice(0,8)}))} placeholder="12345678" maxLength={8}
                  style={{ ...inp, fontFamily:"monospace", borderColor:form.itsId&&form.itsId.length===8?C.green:form.itsId?C.orange:C.border }} />
                {form.itsId&&form.itsId.length!==8&&<div style={{ fontSize:10, color:C.orange, marginTop:2 }}>{form.itsId.length}/8</div>}
              </div>
              {fld("Full Name","fullName","Ahmed Al Rashidi","text",true)}
              {fld("Email","email","volunteer@email.com","email",true)}
              {fld("WhatsApp Number","whatsappNumber","+971501234567","text",true)}
              {fld("Phone Number","phoneNumber","+971501234567","text",true)}
              {fld("Jamiat","jamiat","e.g. Mumbai Jamiat","text",true)}
              {fld("Jamaat","jamaat","e.g. Al Noor Jamaat","text",true)}
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Designation <span style={{color:C.red}}>*</span></div>
                <select value={form.designation||""} onChange={s("designation")} style={{ ...inp, cursor:"pointer" }}>
                  <option value="">Select...</option>
                  {DESIGNATIONS.map(d=><option key={d}>{d}</option>)}
                </select>
              </div>
              {fld("Zone","zone","e.g. North Zone","text",true)}
              {fld("Coordinator","coordinator","Coordinator name")}
              {fld("Age","age","e.g. 28","number")}
              <div style={{ gridColumn:"1 / -1", borderTop:`1px solid ${C.border}`, paddingTop:12, marginTop:4 }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.accent }}>Availability for this event</div>
              </div>
              {fld("Arrival Date","arrivalDate","","date")}
              {fld("Departure Date","departureDate","","date")}
              <div style={{ gridColumn:"1 / -1" }}>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Availability Notes</div>
                <textarea value={form.availabilityNotes||""} onChange={s("availabilityNotes")} placeholder="Any notes about availability..." rows={2} style={{ ...inp, resize:"vertical" }} />
              </div>
              <div>
                <div style={{ fontSize:11, color:C.muted, marginBottom:4, fontWeight:600, textTransform:"uppercase" }}>Status</div>
                <select value={form.registrationStatus||"pending"} onChange={s("registrationStatus")} style={{ ...inp, cursor:"pointer" }}>
                  <option value="pending">Pending</option>
                  <option value="arrived">Arrived</option>
                  <option value="not_coming">Not Coming</option>
                </select>
              </div>
            </div>
            <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:20 }}>
              <button onClick={onClose} style={{ padding:"10px 18px", background:"none", border:`1px solid ${C.border}`, borderRadius:10, color:C.muted, fontSize:13, cursor:"pointer" }}>Cancel</button>
              <button onClick={handleSave} disabled={saving} style={{ padding:"10px 24px", background:C.accent, border:"none", borderRadius:10, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                {saving?"Saving...":"Add Volunteer"}
              </button>
            </div>
          </>
        )}

        {tab==="import" && (
          <div>
            <div style={{ padding:"12px 16px", background:C.accentSoft, border:`1px solid ${C.accent}33`, borderRadius:10, marginBottom:16, fontSize:12, color:C.accent }}>
              All imported volunteers will be assigned to <strong>{event.name}</strong>.
            </div>
            <div style={{ display:"flex", gap:10, flexWrap:"wrap" }}>
              <button onClick={downloadVolunteerTemplate} style={{ padding:"9px 18px", background:C.orangeSoft, border:`1px solid ${C.orange}44`, borderRadius:9, color:C.orange, fontSize:13, fontWeight:600, cursor:"pointer" }}>
                Download Template
              </button>
              <label style={{ padding:"9px 18px", background:C.accentSoft, border:`1px solid ${C.accent}44`, borderRadius:9, color:C.accent, fontSize:13, fontWeight:700, cursor:"pointer" }}>
                {importing?"Importing...":"Choose File and Import"}
                <input type="file" accept=".xlsx,.xls,.csv" onChange={handleImport} style={{ display:"none" }} />
              </label>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Event Detail ─────────────────────
export default function EventDetail({ event: initialEvent, onBack, C }) {
  const { C: themeC }     = useTheme();
  const TC                = C || themeC;
  const { version, bump } = useVolunteerData();

  const [event, setEvent]         = useState(initialEvent);
  const [venues, setVenues]       = useState(null); // null=loading, []=loaded empty
  const [volunteers, setVols]     = useState([]);
  const [loading, setLoading]     = useState(false);
  const [activeTab, setTab]       = useState("venues");
  const [showEdit, setShowEdit]   = useState(false);
  const [showAddVol, setAddVol]   = useState(false);
  const [showAddVenue, setAddVenue] = useState(false);
  const [savingVenue, setSavingVenue] = useState(false);
  const [showDel, setShowDel]     = useState(false);
  const [deleting, setDeleting]   = useState(false);
  const [volSearch, setVolSearch] = useState("");
  const [editingVol, setEditingVol] = useState(null);   // { id, arrivalDate, departureDate, registrationStatus }
  const [savingVol, setSavingVol]   = useState(false);
  const [volMsg, setVolMsg]         = useState(null);

  const loadData = useCallback(() => {
    if(!event) return;
    setLoading(true);
    Promise.all([getVenuesByEvent(event.id), getVolunteers()])
      .then(([vRes, volRes]) => {
        setVenues(vRes.data.venues||[]);
        const all = volRes.data.volunteers||[];
        setVols(all.filter(v=>v.eventId===event.id||v.eventName===event.name));
      }).catch(()=>{}).finally(()=>setLoading(false));
  }, [event?.id]);

  // Re-run loadData whenever:
  //   • event changes (loadData identity changes)
  //   • global volunteer data version bumps
  useEffect(()=>{ loadData(); },[loadData, version]);

  // ── Remove a volunteer from this event ────
  const removeVol = async (vol) => {
    if(!window.confirm(`Remove ${vol.fullName} from "${event.name}"?\n\nThis cannot be undone.`)) return;
    try {
      await deleteVolunteer(vol.id);
      setVolMsg({ type:"success", text:`Removed ${vol.fullName}.` });
      setTimeout(()=>setVolMsg(null), 2500);
      bump();                         // ← sync to global Volunteers page
      loadData();
    } catch(err) {
      setVolMsg({ type:"error", text:err.response?.data?.message || "Failed to remove volunteer." });
      setTimeout(()=>setVolMsg(null), 3500);
    }
  };

  // ── Save inline volunteer edit ─────────────
  const saveVolEdit = async () => {
    if(!editingVol) return;
    setSavingVol(true);
    try {
      await patchVolunteer(editingVol.id, {
        arrivalDate:        editingVol.arrivalDate,
        departureDate:      editingVol.departureDate,
        registrationStatus: editingVol.registrationStatus,
      });
      setVolMsg({type:"success", text:"Saved!"});
      setTimeout(()=>setVolMsg(null), 2000);
      setEditingVol(null);
      bump();                         // ← sync to global Volunteers page
      loadData();
    } catch {
      setVolMsg({type:"error", text:"Failed to save."});
      setTimeout(()=>setVolMsg(null), 3000);
    }
    setSavingVol(false);
  };

  const handleAddVenue = async (data) => {
    setSavingVenue(true);
    try { await createVenue({...data,eventId:event.id}); loadData(); setAddVenue(false); }
    catch { alert("Failed to add venue."); }
    setSavingVenue(false);
  };

  const filteredVols = volunteers.filter(v => {
    const q = volSearch.toLowerCase();
    return !q || v.fullName?.toLowerCase().includes(q) || v.itsId?.includes(q) || v.email?.toLowerCase().includes(q) || v.whatsappNumber?.includes(q);
  });

  if(!event) return null;

  const TABS = [
    { id:"venues",     label:`Venues (${venues===null?"…":venues.length})` },
    { id:"volunteers", label:`Volunteers (${volunteers.length})` },
    { id:"info",       label:"Info" },
  ];

  return (
    <div>
      {showEdit && <EditEventModal event={event} C={TC} onClose={()=>setShowEdit(false)} onSaved={(u)=>{ setEvent(u); setShowEdit(false); }} />}
      {showAddVol && <AddVolunteerModal event={event} C={TC} onClose={()=>setAddVol(false)} onAdded={()=>{ bump(); loadData(); setAddVol(false); }} />}
      {showDel && (
        <div className="modal-overlay" style={{ background:TC.modalBg }}>
          <div style={{ background:TC.card, border:`1px solid ${TC.red}44`, borderRadius:18, padding:"28px 32px", maxWidth:400, width:"100%", textAlign:"center" }}>
            <div style={{ fontSize:40, marginBottom:14 }}>Warning</div>
            <div style={{ fontSize:17, fontWeight:800, color:TC.text, marginBottom:8 }}>Delete This Event?</div>
            <div style={{ fontSize:13, color:TC.muted, marginBottom:24 }}>Permanently delete <strong style={{color:TC.text}}>{event.name}</strong> and all its data.</div>
            <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
              <button onClick={()=>setShowDel(false)} style={{ padding:"10px 20px", background:"none", border:`1px solid ${TC.border}`, borderRadius:10, color:TC.muted, fontSize:13, cursor:"pointer" }}>Keep</button>
              <button onClick={async()=>{ setDeleting(true); try{ await deleteEvent(event.id); onBack(); }catch{ alert("Failed."); } setDeleting(false); }} disabled={deleting}
                style={{ padding:"10px 20px", background:TC.red, border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                {deleting?"Deleting...":"Yes, Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Breadcrumb */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:20, fontSize:13 }}>
        <button onClick={onBack} style={{ background:"none", border:"none", color:TC.accent, cursor:"pointer", fontWeight:600, padding:0, fontSize:13 }}>Back to Events</button>
        <span style={{ color:TC.muted }}>/</span>
        <span style={{ color:TC.text, fontWeight:700 }}>{event.name}</span>
      </div>

      {/* Event Hero */}
      <div style={{ background:TC.card, border:`1px solid ${TC.accent}33`, borderRadius:16, padding:"20px 24px", marginBottom:22 }}>
        <div className="flex-between" style={{ flexWrap:"wrap", gap:12 }}>
          <div style={{ flex:1 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8, flexWrap:"wrap" }}>
              <span style={{ fontSize:20, fontWeight:800, color:TC.text }}>{event.name}</span>
              <Badge text={event.status} color={sColor(event.status,TC)} />
              {event.eventType && <Badge text={event.eventType} color={tColor(event.eventType,TC)} />}
            </div>
            {event.description && <div style={{ fontSize:13, color:TC.muted, marginBottom:8 }}>{event.description}</div>}
            <div style={{ display:"flex", gap:16, fontSize:12, color:TC.muted, flexWrap:"wrap" }}>
              {event.startDate && <span>{event.startDate}{event.endDate?` to ${event.endDate}`:""}</span>}
              {event.startTime && <span>{event.startTime}{event.endTime?` to ${event.endTime}`:""} {event.timezone}</span>}
            </div>
          </div>
          <div style={{ display:"flex", flexDirection:"column", gap:10, alignItems:"flex-end" }}>
            <div style={{ display:"flex", gap:10 }}>
              {[{label:"Venues",value:venues===null?"…":venues.length,color:TC.accent},{label:"Volunteers",value:volunteers.length,color:TC.green}].map((s,i)=>(
                <div key={i} style={{ padding:"10px 14px", background:TC.bg, border:`1px solid ${TC.border}`, borderRadius:11, textAlign:"center", minWidth:70 }}>
                  <div style={{ fontSize:20, fontWeight:800, color:s.color }}>{s.value}</div>
                  <div style={{ fontSize:9, color:TC.muted, fontWeight:600, textTransform:"uppercase", marginTop:2 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={()=>setShowEdit(true)} style={{ padding:"8px 16px", background:TC.accentSoft, border:`1px solid ${TC.accent}44`, borderRadius:9, color:TC.accent, fontSize:12, fontWeight:700, cursor:"pointer" }}>Edit</button>
              {(() => {
                // venues=null → still loading, venues=[] → loaded+empty, venues=[...] → has venues
                const canDel = venues !== null && venues.length === 0;
                const isLoading = venues === null;
                const hasVenues = venues !== null && venues.length > 0;
                const tip = isLoading ? "Loading venues..." : hasVenues ? `Delete all ${venues.length} venue${venues.length>1?"s":""} and their gates first` : "";
                return (
                  <div title={tip}>
                    <button
                      onClick={()=>{ if(canDel) setShowDel(true); }}
                      disabled={!canDel}
                      style={{ padding:"8px 16px", borderRadius:9, fontSize:12, fontWeight:700, border:"none",
                        background: canDel ? TC.redSoft : TC.border,
                        color:      canDel ? TC.red     : TC.muted,
                        cursor:     canDel ? "pointer"  : "not-allowed",
                        opacity:    canDel ? 1 : 0.5,
                      }}>
                      {isLoading ? "Loading..." : hasVenues ? `Delete (remove ${venues.length} venue${venues.length>1?"s":""} first)` : "Delete"}
                    </button>
                  </div>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:18, background:TC.card, padding:4, borderRadius:12, width:"fit-content", border:`1px solid ${TC.border}` }}>
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)}
            style={{ padding:"8px 16px", background:activeTab===t.id?TC.accent:"transparent", border:"none", borderRadius:9, color:activeTab===t.id?"#000":TC.muted, fontSize:13, fontWeight:activeTab===t.id?700:500, cursor:"pointer", transition:"all 0.2s", whiteSpace:"nowrap" }}>
            {t.label}
          </button>
        ))}
      </div>

      {/* VENUES TAB */}
      {activeTab==="venues" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ fontSize:13, color:TC.muted }}>Click a venue to expand and manage gates</div>
            <button onClick={()=>setAddVenue(v=>!v)} style={{ padding:"8px 16px", background:TC.accent, border:"none", borderRadius:9, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>
              {showAddVenue?"Cancel":"+ Add Venue"}
            </button>
          </div>
          {showAddVenue && <div style={{ marginBottom:16 }}><VenueForm onSave={handleAddVenue} onCancel={()=>setAddVenue(false)} saving={savingVenue} C={TC} /></div>}
          {loading ? <div style={{ padding:40, textAlign:"center", color:TC.muted }}>Loading...</div>
          : (venues||[]).length===0 ? (
            <div style={{ padding:60, textAlign:"center", background:TC.card, border:`1px solid ${TC.border}`, borderRadius:16 }}>
              <div style={{ fontSize:40, marginBottom:14 }}>🏟</div>
              <div style={{ fontSize:15, fontWeight:700, color:TC.text, marginBottom:6 }}>No venues yet</div>
              <div style={{ fontSize:13, color:TC.muted }}>Click Add Venue to get started.</div>
            </div>
          ) : (venues||[]).map((venue,i)=>(
            <VenueSection key={venue.id||i} venue={venue} eventId={event.id}
              onVenueUpdated={(u)=>setVenues(vs=>vs.map(v=>v.id===u.id?u:v))}
              onVenueDeleted={(id)=>setVenues(vs=>vs.filter(v=>v.id!==id))}
              C={TC} />
          ))}
        </div>
      )}

      {/* VOLUNTEERS TAB */}
      {activeTab==="volunteers" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14, flexWrap:"wrap", gap:10 }}>
            <input value={volSearch} onChange={e=>setVolSearch(e.target.value)}
              placeholder="Search by name, ITS ID, email or WhatsApp..."
              style={{ flex:1, minWidth:200, padding:"9px 13px", background:TC.card, border:`1px solid ${TC.border}`, borderRadius:9, color:TC.text, fontSize:13, outline:"none" }} />
            <button onClick={()=>setAddVol(true)} style={{ padding:"9px 18px", background:TC.accent, border:"none", borderRadius:9, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>
              + Add Volunteers
            </button>
          </div>
          {volunteers.length===0 ? (
            <div style={{ padding:60, textAlign:"center", background:TC.card, border:`1px solid ${TC.border}`, borderRadius:16 }}>
              <div style={{ fontSize:40, marginBottom:14 }}>👥</div>
              <div style={{ fontSize:15, fontWeight:700, color:TC.text, marginBottom:6 }}>No volunteers yet</div>
              <div style={{ fontSize:13, color:TC.muted, marginBottom:20 }}>Add from contacts, manually or import a file.</div>
              <button onClick={()=>setAddVol(true)} style={{ padding:"10px 24px", background:TC.accent, border:"none", borderRadius:10, color:"#000", fontSize:13, fontWeight:700, cursor:"pointer" }}>+ Add Volunteers</button>
            </div>
          ) : (
            <div style={{ background:TC.card, border:`1px solid ${TC.border}`, borderRadius:16, overflow:"hidden" }}>
              <div style={{ padding:"11px 16px", borderBottom:`1px solid ${TC.border}`, fontSize:12, color:TC.muted }}>
                {filteredVols.length} of {volunteers.length} volunteers · {event.name}
              </div>
              {/* Inline edit message */}
              {volMsg && (
                <div style={{ padding:"9px 14px", marginBottom:8, borderRadius:8, background:volMsg.type==="success"?TC.greenSoft:TC.redSoft, color:volMsg.type==="success"?TC.green:TC.red, fontSize:13, fontWeight:600 }}>
                  {volMsg.text}
                </div>
              )}
              <div className="responsive-table">
                <table>
                  <thead>
                    <tr style={{ background:TC.bg }}>
                      {["ITS ID","Name","WhatsApp","Phone","Email","Jamiat","Jamaat","Designation","Zone","Arrival","Departure","Status","Actions"].map(h=>(
                        <th key={h} style={{ padding:"10px 14px", textAlign:"left", fontSize:10, fontWeight:700, color:TC.muted, letterSpacing:"0.8px", textTransform:"uppercase", borderBottom:`1px solid ${TC.border}`, whiteSpace:"nowrap" }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVols.map((v,i)=>{
                      const isEditing = editingVol?.id === v.id;
                      const inpStyle  = { padding:"5px 8px", background:TC.inputBg, border:`1px solid ${TC.accent}`, borderRadius:6, color:TC.text, fontSize:11, outline:"none" };
                      return (
                        <tr key={i} style={{ borderBottom:`1px solid ${TC.border}`, background:isEditing?`${TC.accent}09`:"transparent", transition:"background 0.15s" }}
                          onMouseEnter={e=>!isEditing&&(e.currentTarget.style.background=TC.border+"44")}
                          onMouseLeave={e=>!isEditing&&(e.currentTarget.style.background="transparent")}>
                          <td style={{ padding:"10px 14px", fontSize:11, color:TC.muted, fontFamily:"monospace" }}>{v.itsId||"—"}</td>
                          <td style={{ padding:"10px 14px" }}>
                            <div style={{ fontSize:13, fontWeight:700, color:TC.text }}>{v.fullName}</div>
                          </td>
                          <td style={{ padding:"10px 14px", fontSize:12, color:TC.green, fontWeight:600 }}>{v.whatsappNumber||"—"}</td>
                          <td style={{ padding:"10px 14px", fontSize:12, color:TC.muted }}>{v.phoneNumber||"—"}</td>
                          <td style={{ padding:"10px 14px", fontSize:11, color:TC.muted }}>{v.email||"—"}</td>
                          <td style={{ padding:"10px 14px", fontSize:12, color:TC.text }}>{v.jamiat||"—"}</td>
                          <td style={{ padding:"10px 14px", fontSize:12, color:TC.text }}>{v.jamaat||"—"}</td>
                          <td style={{ padding:"10px 14px" }}>{v.designation?<Badge text={v.designation} color={TC.purple}/>:<span style={{fontSize:11,color:TC.muted}}>—</span>}</td>
                          <td style={{ padding:"10px 14px", fontSize:12, color:TC.text }}>{v.zone||"—"}</td>

                          {/* Editable: Arrival Date */}
                          <td style={{ padding:"10px 14px" }}>
                            {isEditing ? (
                              <input type="date" value={editingVol.arrivalDate||""} onChange={e=>setEditingVol(ev=>({...ev,arrivalDate:e.target.value}))} style={inpStyle} />
                            ) : (
                              <span style={{ fontSize:12, color:TC.accent, fontWeight:600 }}>{v.arrivalDate||"—"}</span>
                            )}
                          </td>

                          {/* Editable: Departure Date */}
                          <td style={{ padding:"10px 14px" }}>
                            {isEditing ? (
                              <input type="date" value={editingVol.departureDate||""} onChange={e=>setEditingVol(ev=>({...ev,departureDate:e.target.value}))} style={inpStyle} />
                            ) : (
                              <span style={{ fontSize:12, color:TC.orange, fontWeight:600 }}>{v.departureDate||"—"}</span>
                            )}
                          </td>

                          {/* Editable: Status */}
                          <td style={{ padding:"10px 14px" }}>
                            {isEditing ? (
                              <select value={editingVol.registrationStatus||"pending"} onChange={e=>setEditingVol(ev=>({...ev,registrationStatus:e.target.value}))} style={{ ...inpStyle, cursor:"pointer" }}>
                                <option value="pending">Pending</option>
                                <option value="arrived">Arrived</option>
                                <option value="not_coming">Not Coming</option>
                              </select>
                            ) : (
                              <Badge text={regLabel(v.registrationStatus)} color={regColor(v.registrationStatus,TC)} />
                            )}
                          </td>

                          {/* Edit + Remove actions */}
                          <td style={{ padding:"10px 14px", whiteSpace:"nowrap" }}>
                            {isEditing ? (
                              <div style={{ display:"flex", gap:5 }}>
                                <button onClick={saveVolEdit} disabled={savingVol}
                                  style={{ padding:"5px 10px", background:TC.green, border:"none", borderRadius:6, color:"#000", fontSize:11, fontWeight:700, cursor:"pointer" }}>
                                  {savingVol?"...":"Save"}
                                </button>
                                <button onClick={()=>setEditingVol(null)}
                                  style={{ padding:"5px 8px", background:"none", border:`1px solid ${TC.border}`, borderRadius:6, color:TC.muted, fontSize:11, cursor:"pointer" }}>
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div style={{ display:"flex", gap:5 }}>
                                <button onClick={()=>setEditingVol({ id:v.id, arrivalDate:v.arrivalDate||"", departureDate:v.departureDate||"", registrationStatus:v.registrationStatus||"pending" })}
                                  style={{ padding:"5px 10px", background:TC.accentSoft, border:`1px solid ${TC.accent}44`, borderRadius:6, color:TC.accent, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                                  Edit
                                </button>
                                <button onClick={()=>removeVol(v)}
                                  title={`Remove ${v.fullName} from this event`}
                                  style={{ padding:"5px 10px", background:TC.redSoft, border:`1px solid ${TC.red}33`, borderRadius:6, color:TC.red, fontSize:11, fontWeight:700, cursor:"pointer" }}>
                                  Remove
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* INFO TAB */}
      {activeTab==="info" && (
        <div style={{ background:TC.card, border:`1px solid ${TC.border}`, borderRadius:16, padding:"20px 24px" }}>
          {[
            {label:"Event Name",value:event.name},{label:"Event Type",value:event.eventType},
            {label:"Status",value:event.status},{label:"Start Date",value:event.startDate},
            {label:"End Date",value:event.endDate||"—"},{label:"Start Time",value:event.startTime||"—"},
            {label:"End Time",value:event.endTime||"—"},{label:"Timezone",value:event.timezone||"UTC"},
            {label:"Description",value:event.description||"—"},
          ].map((row,i)=>(
            <div key={i} style={{ display:"flex", gap:12, padding:"10px 0", borderBottom:`1px solid ${TC.border}` }}>
              <div style={{ fontSize:12, color:TC.muted, fontWeight:600, minWidth:130 }}>{row.label}</div>
              <div style={{ fontSize:13, color:TC.text }}>{row.value||"—"}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}